import React, { useCallback, useMemo } from 'react';
import { ScrollView, StatusBar, TouchableOpacity, View } from 'react-native';
import FastImage from 'utils/packages/rnFastImage';
import LinearGradient from 'utils/packages/linearGradient';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import Text from 'design-system/Text';
import { getRewardLink } from 'actions';
import { AppView, rem, Spacer } from 'design-system';
import BottomPanel from 'design-system/BottomPanel';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useStyles from 'hooks/useStyles';
import Amplitude from 'utils/amplitude';
import createStyleSheets from 'utils/createStyleSheets';
import Links from 'utils/links';
import { useAppSelector } from 'store/hooks';
import Modal from 'utils/packages/Modal';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { UnavailableOnWebModal } from 'utils/packages/UnavailableOnWebModal';
import { isWeb } from '../../../constants';
const GRADIENT_END = { x: 1, y: 1 };
const GRADIENT_START = { x: 1, y: 0 };
const GRADIENT_COLORS = ['rgba(0,0,0,0)', 'rgba(0,0,0,0.62)'];
const GB_IMAGE = require('../img/pensionBee.png');
const US_IMAGE = require('../img/pensionBeeUS.png');
const REWARD_IDS = {
    GB: 27,
    US: 647,
};
const PensionBeeScreen = ({ navigation }) => {
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const openTerms = useCallback(() => {
        // Connecting an account is mobile-only
        if (isWeb) {
            UnavailableOnWebModal.show('emma://save?tab=opportunities', 'Connect my PensionBee account');
            return;
        }
        navigation.navigate('ChooseBank', { back: true, investments: true });
    }, []);
    const onClaimReward = useCallback(async () => {
        const id = REWARD_IDS[guessedHomeCountry];
        if (id == null) {
            Modal.showError('Offer not available in your country');
            return;
        }
        const res = await fetch(getRewardLink(id));
        if (!res?.url)
            return;
        Links.openLink(res.url);
        Amplitude.logEvent('SavingsCheckList.openLink', { partner: 'PensionBee' });
    }, [guessedHomeCountry]);
    const title = useMemo(() => {
        if (guessedHomeCountry === 'US') {
            return 'Get a 1% match on your IRA contributions';
        }
        return 'All your pensions in one place';
    }, [guessedHomeCountry]);
    const content = useMemo(() => {
        if (guessedHomeCountry === 'US') {
            return (<>
          <Text TextThin-14 Secondary>
            PensionBee’s Limited Time Offer
          </Text>
          <Spacer h={10}/>
          <Text TextThin-14 Secondary>
            Emma customers can receive a 1% match when they contribute to or
            roll over into a PensionBee IRA. Terms & conditions apply.
          </Text>
          <Spacer h={10}/>
          <Text TextThin-14 Secondary>
            Once you’re set up, view your PensionBee pension within the Emma
            app, so you can better understand your savings of tomorrow with your
            spending of today.
          </Text>
          <Spacer h={10}/>
          <Text TextThin-14 Secondary>
            See your 1% match today and potential growth in 30 years (assumes an
            illustrative 7% annual return less 0.85% annual fee, for a 6.15% net
            return):
          </Text>
          <Spacer h={10}/>
          <Text TextThin-14 Secondary>
            {`• $10,000 rollover → $100 match today → grows to $599 in 30 years\n• $100,000 rollover → $1,000 match today → grows to $5,992 in 30 years\n• $250,000 rollover → $2,500 match today → grows to $14,981 in 30 years\n• $500,000 rollover → $5,000 match today → grows to $29,962 in 30 years`}
          </Text>
          <Spacer h={10}/>
          <Text TextThin-14 Secondary>
            Illustrative example only, investing involves risk. Project images
            are hypothetical.
          </Text>
          <Spacer h={10}/>
          <Text TextThin-14 Secondary>
            Before investing, consider the funds’ investment objectives, risks,
            charges and expenses. To obtain a prospectus or summary prospectus,
            which contains this and other information, call 1.866.787.2257 or
            visit www.statestreet.com/im.
          </Text>
        </>);
        }
        return (<>
        <Text TextThin-14 Secondary>
          PensionBee combines your old pensions into one simple online plan. Use
          the app to track performance, adjust contributions, and make
          withdrawals when you reach retirement.
        </Text>
        <Spacer h={10}/>
        <Text TextThin-14 Secondary>
          Sign up in minutes and choose a plan managed by trusted money managers
          including BlackRock, State Street Global Advisors, and HSBC.
        </Text>
        <Spacer h={10}/>
        <Text TextThin-14 Secondary>
          Once you’re set up, view your PensionBee pension within the Emma app,
          so you can better understand your savings of tomorrow with your
          spending of today.
        </Text>
        <Spacer h={10}/>
        <Text TextThin-14 Secondary>
          With pensions, your capital is at risk.
        </Text>
      </>);
    }, [guessedHomeCountry]);
    const contentContainerStyle = useMarginBottom('paddingBottom', guessedHomeCountry === 'US' ? HEIGHT_PLUS_PADDING : undefined, styles.descriptionContainer);
    return (<AppView style={styles.content} withFrame={false}>
      <StatusBar barStyle="light-content"/>
      <View style={styles.wrapper}>
        <FastImage source={guessedHomeCountry === 'US' ? US_IMAGE : GB_IMAGE} style={styles.image}>
          <LinearGradient end={GRADIENT_END} start={GRADIENT_START} colors={GRADIENT_COLORS} style={styles.container}/>
          <View style={styles.bottom}/>
        </FastImage>
        <View style={[
            styles.container,
            { alignSelf: 'center' },
            isWeb && styles.webColumn,
            isWeb && paddingHorizontalStyle,
        ]}>
          <View style={styles.header}>
            <Text Text-20>{title}</Text>
          </View>
          <ScrollView contentContainerStyle={contentContainerStyle}>
            {content}
          </ScrollView>
        </View>
        {guessedHomeCountry === 'GB' ? (<BottomPanel isInTab={isWeb}>
            <TouchableOpacity onPress={openTerms}>
              <Text TextThin-16 Secondary centered>
                Connect my PensionBee account
              </Text>
            </TouchableOpacity>
            <Button brand onPress={onClaimReward} title="Go to PensionBee" style={styles.button} isFetching={isFetching}/>
          </BottomPanel>) : (<Button brand floating isInTab={isWeb} onPress={onClaimReward} title="Go to PensionBee" isFetching={isFetching}/>)}
      </View>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    content: {
        flex: 1,
    },
    container: {
        flex: 1,
    },
    wrapper: {
        flex: 1,
        backgroundColor: colors.background.light,
    },
    header: {
        margin: rem(16),
        justifyContent: 'center',
    },
    image: {
        height: '30%',
        width: '100%',
    },
    webColumn: {
        alignSelf: 'stretch',
    },
    descriptionContainer: {
        marginHorizontal: rem(16),
    },
    button: {
        marginTop: rem(16),
    },
    bottom: {
        left: 0,
        right: 0,
        bottom: 0,
        height: rem(15),
        position: 'absolute',
        borderTopLeftRadius: rem(10),
        borderTopRightRadius: rem(10),
        backgroundColor: colors.background.light,
    },
}));
export default PensionBeeScreen;
